/**
 * M-02 «Это движ!».
 *
 * ПС §10: «Обе стороны видят экран/уведомление совпадения» и получают
 * доступ к личному диалогу. Изменение v1.3: вместо контекста режима экран
 * предлагает первые фразы из подбора (BS §11.4) — нажатие кладёт фразу
 * черновиком в диалог, отправка остаётся явным действием.
 */
import { createPortal } from 'react-dom'

import type { Match, ProfilePhoto, PublicPhoto } from '@shared/api/types'
import { Icon } from '@shared/ui'
import { useModalLayer } from '@shared/lib/modal-layer'

import { useAppStore } from '../../model/app-store'
import styles from './MatchCelebration.module.css'

/** Средний вариант фото; в галерее совпадения кадр крупный, миниатюра мылит. */
function photoUrlOf(photo: PublicPhoto | ProfilePhoto | undefined): string | null {
  if (!photo) return null
  return photo.variants.find((v) => v.name === 'medium')?.url ?? photo.variants[0]?.url ?? null
}

interface MatchCelebrationProps {
  match: Match
  /** Первые фразы из подбора карточки, по которой нажали «Го». */
  icebreakers?: readonly string[]
  onClose: () => void
  /** Открыть диалог; `draft` — выбранная фраза для черновика. */
  onOpenChat: (draft?: string) => void
}

export function MatchCelebration({ match, icebreakers = [], onClose, onOpenChat }: MatchCelebrationProps) {
  // §4.4: системный «назад» и `Escape` закрывают экран совпадения, фон под ним недоступен.
  const { ref, layerRef } = useModalLayer(true, onClose)

  const counterpart = match.counterpart
  const photoUrl = photoUrlOf(counterpart.photos[0])
  /*
   * Слева — своё фото: раньше на его месте всегда был безликий значок, и «две
   * карточки рядом» превращались в заглушку. Берём первое одобренное из `me`.
   */
  const me = useAppStore((s) => s.me)
  const myPhoto = me?.profile.photos.find((p) => p.state === 'approved') ?? me?.profile.photos[0]
  const myPhotoUrl = photoUrlOf(myPhoto)

  return createPortal(
    <div ref={layerRef} data-modal-layer>
      <div
        ref={ref}
        className={styles['backdrop']}
        role="dialog"
        aria-modal="true"
        aria-labelledby="match-celebration-title"
        tabIndex={-1}
      >
        <button type="button" className={styles['close']} onClick={onClose} aria-label="Закрыть">
          <Icon name="close" size={22} />
        </button>

        <div className={styles['avatars']}>
          <div className={styles['avatarSlot']}>
            {myPhotoUrl ? (
              <img className={styles['photo']} src={myPhotoUrl} alt="" />
            ) : (
              <div className={styles['photoFallback']}>
                <Icon name="user" size={48} />
              </div>
            )}
          </div>

          <div className={styles['avatarSlot']}>
            {photoUrl ? (
              <img className={styles['photo']} src={photoUrl} alt="" />
            ) : (
              <div className={styles['photoFallback']}>
                <Icon name="user" size={48} />
              </div>
            )}
          </div>
        </div>

        <div className={styles['headline']}>
          <h1 id="match-celebration-title" className={styles['title']}>
            Это движ!
          </h1>
          <p className={styles['subtitle']}>
            {counterpart.display_name ? `${counterpart.display_name} тоже за движ` : 'Взаимное «Го»'} — теперь
            можно писать
          </p>
        </div>

        {icebreakers.length ? (
          <div className={styles['starters']}>
            <span className={styles['startersTitle']}>
              <Icon name="sparkle" size={14} />С чего начать
            </span>
            {icebreakers.map((text) => (
              <button key={text} type="button" className={styles['starter']} onClick={() => onOpenChat(text)}>
                {text}
              </button>
            ))}
          </div>
        ) : null}

        <div className={styles['actions']}>
          <button type="button" className={styles['primary']} onClick={() => onOpenChat()}>
            Написать
          </button>
          <button type="button" className={styles['secondary']} onClick={onClose}>
            Листать дальше
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
